import React, { useState, useEffect } from "react";
import {
  BookOpenIcon,
  SpeakerWaveIcon,
  InformationCircleIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";

const Lessons = () => {
  const [lessons, setLessons] = useState([]);
  const [words, setWords] = useState([]);
  const [activeLesson, setActiveLesson] = useState(null);
  const [selectedWord, setSelectedWord] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [wordsPerPage] = useState(9);

  useEffect(() => {
    loadLessons();
  }, []);

  useEffect(() => {
    if (lessons.length > 0 && !activeLesson) {
      const firstLesson = lessons[0];
      loadLevelWords(firstLesson.level_no);
    }
  }, [lessons, activeLesson]);

  const loadLessons = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        "https://openapi.programming-hero.com/api/levels/all"
      );
      const data = await response.json();
      setLessons(data.data || []);
    } catch (err) {
      setError("Failed to load lessons");
    } finally {
      setLoading(false);
    }
  };

  const loadLevelWords = async (levelId) => {
    try {
      setLoading(true);
      setError(null);
      setCurrentPage(1); // Reset to first page when loading new lesson
      const response = await fetch(
        `https://openapi.programming-hero.com/api/level/${levelId}`
      );
      const data = await response.json();
      setWords(data.data || []);
      setActiveLesson(levelId);
    } catch (err) {
      setError("Failed to load words");
    } finally {
      setLoading(false);
    }
  };

  const loadWordDetails = async (wordId) => {
    try {
      const response = await fetch(
        `https://openapi.programming-hero.com/api/word/${wordId}`
      );
      const data = await response.json();
      setSelectedWord(data.data);
      setIsModalOpen(true);
    } catch (err) {
      console.error("Failed to load word details:", err);
    }
  };

  const speakWord = (word) => {
    if ("speechSynthesis" in window) {
      const utterance = new SpeechSynthesisUtterance(word);
      utterance.lang = "en-US";
      speechSynthesis.speak(utterance);
    }
  };

  // Pagination logic
  const indexOfLastWord = currentPage * wordsPerPage;
  const indexOfFirstWord = indexOfLastWord - wordsPerPage;
  const currentWords = words.slice(indexOfFirstWord, indexOfLastWord);
  const totalPages = Math.ceil(words.length / wordsPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    // Smooth scroll to top of words container
    const wordsContainer = document.querySelector("#words-container");
    if (wordsContainer) {
      wordsContainer.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const nextPage = () => {
    if (currentPage < totalPages) {
      paginate(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      paginate(currentPage - 1);
    }
  };

  return (
    <section id="lessons" className="py-20 bg-white dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-16" data-aos="fade-up">
          <h2 className="text-4xl lg:text-6xl font-bold font-display mb-6">
            Interactive <span className="gradient-text">Lessons</span>
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Explore our comprehensive vocabulary lessons designed to enhance
            your English proficiency
          </p>
        </div>

        {/* Lesson Buttons */}
        <div
          className="flex flex-wrap justify-center gap-4 mb-12"
          data-aos="fade-up"
          data-aos-delay="200"
        >
          {loading && lessons.length === 0 ? (
            <div className="flex items-center space-x-3 text-indigo-600">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              <span className="font-medium">Loading lessons...</span>
            </div>
          ) : (
            lessons.map((lesson, index) => (
              <button
                key={lesson.level_no}
                onClick={() => loadLevelWords(lesson.level_no)}
                disabled={loading}
                className={`flex items-center space-x-3 px-8 py-4 rounded-full font-semibold transition-all duration-200 transform hover:scale-105 disabled:opacity-50 ${
                  activeLesson === lesson.level_no
                    ? "bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-xl"
                    : "bg-white text-indigo-600 border-2 border-indigo-200 hover:border-indigo-400 hover:bg-indigo-50 shadow-lg"
                }`}
                data-aos="zoom-in"
                data-aos-delay={300 + index * 100}
              >
                {loading && activeLesson === lesson.level_no ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current"></div>
                ) : (
                  <BookOpenIcon className="w-5 h-5" />
                )}
                <span>Level {lesson.level_no}</span>
              </button>
            ))
          )}
        </div>

        {/* Words Container */}
        <div
          id="words-container"
          className="bg-gradient-to-br from-slate-50 to-blue-50 dark:from-gray-800 dark:to-gray-700 rounded-3xl shadow-2xl p-8"
          data-aos="fade-up"
          data-aos-delay="400"
        >
          {loading && (!words.length || !activeLesson) ? (
            <div className="text-center py-20">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto mb-6"></div>
              <p className="text-gray-600 text-lg font-medium">
                {!lessons.length
                  ? "Loading lessons..."
                  : "Loading vocabulary..."}
              </p>
            </div>
          ) : error ? (
            <div className="text-center py-20" data-aos="fade-up">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-red-500 text-3xl">⚠️</span>
              </div>
              <p className="text-red-600 font-semibold text-lg mb-6">{error}</p>
              <button
                onClick={() => loadLessons()}
                className="px-8 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-full hover:from-indigo-700 hover:to-purple-700 transition-all font-semibold"
              >
                Try Again
              </button>
            </div>
          ) : words.length === 0 ? (
            <div className="text-center py-20 space-y-6" data-aos="fade-up">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-8">
                <BookOpenIcon className="w-12 h-12 text-gray-400" />
              </div>
              <p className="text-gray-500 font-bangla text-xl">
                {activeLesson
                  ? "এই Lesson এ এখনো কোন Vocabulary যুক্ত করা হয়নি।"
                  : "একটি Lesson Select করুন।"}
              </p>
            </div>
          ) : (
            <div>
              <div
                className="text-center mb-12"
                data-aos="fade-up"
                data-aos-delay="600"
              >
                <h3 className="text-3xl font-bold font-display text-gray-800 mb-3">
                  Level {activeLesson} Vocabulary
                </h3>
                <p className="text-gray-600 text-lg">
                  {words.length} carefully selected words to master
                </p>
                <p className="text-gray-500 text-sm mt-2">
                  Showing {indexOfFirstWord + 1}-
                  {Math.min(indexOfLastWord, words.length)} of {words.length}{" "}
                  words
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {currentWords.map((word, index) => (
                  <div
                    key={word.id}
                    className="bg-white p-8 rounded-3xl shadow-xl card-hover border border-gray-100"
                    data-aos="fade-up"
                    data-aos-delay={800 + index * 100}
                  >
                    <div className="space-y-6">
                      <h4 className="text-2xl font-bold font-display text-gray-800">
                        {word.word}
                      </h4>
                      <div className="space-y-3">
                        <p className="text-sm text-gray-500 uppercase tracking-wider font-semibold">
                          Meaning / Pronunciation
                        </p>
                        <p className="text-lg leading-relaxed">
                          <span className="text-indigo-600 font-bangla font-semibold block mb-1">
                            {word.meaning || "খালি"}
                          </span>
                          <span className="text-green-600 font-medium">
                            {word.pronunciation || "খালি"}
                          </span>
                        </p>
                      </div>
                      <div className="flex justify-between pt-4">
                        <button
                          onClick={() => loadWordDetails(word.id)}
                          className="flex items-center justify-center w-14 h-14 bg-indigo-100 hover:bg-indigo-200 text-indigo-600 rounded-full transition-all btn-press shadow-lg"
                          title="View Details"
                        >
                          <InformationCircleIcon className="w-6 h-6" />
                        </button>
                        <button
                          onClick={() => speakWord(word.word)}
                          className="flex items-center justify-center w-14 h-14 bg-green-100 hover:bg-green-200 text-green-600 rounded-full transition-all btn-press shadow-lg"
                          title="Pronounce"
                        >
                          <SpeakerWaveIcon className="w-6 h-6" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {words.length > wordsPerPage && (
                <div
                  className="flex justify-center items-center mt-12 space-x-4"
                  data-aos="fade-up"
                  data-aos-delay="1000"
                >
                  <button
                    onClick={prevPage}
                    disabled={currentPage === 1}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-full font-semibold transition-all ${
                      currentPage === 1
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-white text-indigo-600 hover:bg-indigo-50 shadow-lg hover:shadow-xl transform hover:scale-105"
                    }`}
                  >
                    <ChevronLeftIcon className="w-5 h-5" />
                    <span>Previous</span>
                  </button>

                  <div className="flex space-x-2">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (page) => (
                        <button
                          key={page}
                          onClick={() => paginate(page)}
                          className={`w-12 h-12 rounded-full font-semibold transition-all transform hover:scale-105 ${
                            currentPage === page
                              ? "bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg"
                              : "bg-white text-indigo-600 hover:bg-indigo-50 shadow-lg hover:shadow-xl"
                          }`}
                        >
                          {page}
                        </button>
                      )
                    )}
                  </div>

                  <button
                    onClick={nextPage}
                    disabled={currentPage === totalPages}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-full font-semibold transition-all ${
                      currentPage === totalPages
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-white text-indigo-600 hover:bg-indigo-50 shadow-lg hover:shadow-xl transform hover:scale-105"
                    }`}
                  >
                    <span>Next</span>
                    <ChevronRightIcon className="w-5 h-5" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Word Modal */}
        {isModalOpen && selectedWord && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50 modal-backdrop">
            <div
              className="bg-white rounded-3xl max-w-lg w-full p-8 transform transition-all shadow-2xl"
              data-aos="zoom-in"
            >
              <div className="space-y-6">
                <div className="flex justify-between items-start">
                  <h3 className="text-3xl font-bold font-display text-gray-800">
                    {selectedWord.word}
                  </h3>
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-500 uppercase tracking-wider font-semibold mb-2">
                      Meaning
                    </p>
                    <p className="text-lg font-semibold text-indigo-600 font-bangla bg-indigo-50 p-4 rounded-xl">
                      {selectedWord.meaning || "No meaning available"}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500 uppercase tracking-wider font-semibold mb-2">
                      Pronunciation
                    </p>
                    <p className="text-lg font-semibold text-green-600 bg-green-50 p-4 rounded-xl">
                      {selectedWord.pronunciation ||
                        "No pronunciation available"}
                    </p>
                  </div>

                  {selectedWord.example && (
                    <div>
                      <p className="text-sm text-gray-500 uppercase tracking-wider font-semibold mb-2">
                        Example
                      </p>
                      <p className="text-gray-700 italic bg-gray-50 p-4 rounded-xl">
                        "{selectedWord.example}"
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                  <button
                    onClick={() => speakWord(selectedWord.word)}
                    className="flex items-center space-x-2 px-6 py-3 bg-green-100 text-green-600 rounded-full hover:bg-green-200 transition-colors btn-press font-semibold"
                  >
                    <SpeakerWaveIcon className="w-5 h-5" />
                    <span>Pronounce</span>
                  </button>
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="px-6 py-3 bg-gray-100 text-gray-600 rounded-full hover:bg-gray-200 transition-colors btn-press font-semibold"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Lessons;
